import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WelcomeSheet from '@/components/WelcomeSheet';
import NightFeedMode from '@/components/NightFeedMode';

const API = process.env.REACT_APP_API_URL || '';

// ── Warm palette ──────────────────────────────────────────────────────────────
const C = {
  terracota: '#C4866A',
  rose:      '#D4A5A0',
  gold:      '#C9A86C',
  cream:     '#FAF5EF',
  bg:        'radial-gradient(ellipse at 50% 25%, #2A1810 0%, #1C1210 50%, #0E0A08 100%)',
  cardBg:    'rgba(255,255,255,0.038)',
  cardBorder:'rgba(196,134,106,0.18)',
};

// Daily loop phases — adapted for matrescence
const LOOP_PHASES = [
  { id: 'orient',    label: 'Orient',    icon: '○', desc: 'Check in with your body and nervous system' },
  { id: 'practice',  label: 'Practice',  icon: '◎', desc: 'Breathwork, somatic, or guided presence' },
  { id: 'observe',   label: 'Observe',   icon: '◉', desc: 'Notice what moved. Name it.' },
  { id: 'integrate', label: 'Integrate', icon: '●', desc: 'Anchor the shift into your day' },
];

// Phase label map for EPDS timing
const WEEKS_LABEL = (wp) => {
  if (wp === null || wp === 'pregnant') return 'Preparing for birth';
  if (wp <= 6)  return 'Newborn phase';
  if (wp <= 13) return 'First trimester of motherhood';
  if (wp <= 26) return 'Finding your rhythm';
  if (wp <= 39) return 'Deepening into motherhood';
  if (wp <= 52) return 'First year';
  return 'Ongoing matrescence';
};

export default function Matrescence() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [eri, setEri] = useState(null);
  const [epds, setEpds] = useState(null);
  const [todaySession, setTodaySession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nightMode, setNightMode] = useState(false);

  useEffect(() => {
    const cached = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(cached);
    loadData(cached);
  }, []);

  const loadData = async (u) => {
    if (!u?.user_id) { setLoading(false); return; }
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [eriRes, epdsRes, sessionRes] = await Promise.allSettled([
        axios.get(`${API}/eri/latest/${u.user_id}`, { headers }),
        axios.get(`${API}/assessments/epds/${u.user_id}/latest`, { headers }),
        axios.get(`${API}/sessions/today/${u.user_id}`, { headers }),
      ]);

      if (eriRes.status === 'fulfilled') setEri(eriRes.value.data);
      if (epdsRes.status === 'fulfilled' && epdsRes.value.data?.epds_score !== undefined) {
        setEpds(epdsRes.value.data);
      }
      if (sessionRes.status === 'fulfilled') setTodaySession(sessionRes.value.data);
    } catch (_) {}
    setLoading(false);
  };

  const profile = user?.matrescence_profile || {};
  const firstName = (user?.name || '').split(' ')[0];
  const babyName = profile.baby_name || null;
  const weeksPostpartum = profile.weeks_postpartum;
  const phase = WEEKS_LABEL(weeksPostpartum);
  const intention = profile.intention || 'be present';

  // Adaptive interval based on postpartum phase (days)
  const epdsIntervalDays = () => {
    if (weeksPostpartum === null || weeksPostpartum === 'pregnant') return 30;
    const wp = parseInt(weeksPostpartum) || 0;
    if (wp <= 26) return 14;   // 0–6 months: bi-weekly
    if (wp <= 52) return 21;   // 6–12 months: every 3 weeks
    return 30;                 // 12+ months: monthly
  };

  // Days since last EPDS (null = never done)
  const daysSinceEpds = () => {
    if (!epds?.created_at) return null;
    return (Date.now() - new Date(epds.created_at).getTime()) / (1000 * 60 * 60 * 24);
  };

  const epdsOverdue = () => {
    const days = daysSinceEpds();
    if (days === null) return true;
    return days >= epdsIntervalDays();
  };

  const epdsCountdownDays = () => {
    const days = daysSinceEpds();
    if (days === null) return null;
    return Math.max(0, Math.ceil(epdsIntervalDays() - days));
  };

  const eriScore = eri?.eri_score;

  if (loading) {
    return (
      <div style={{
        minHeight: '100dvh', background: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `2px solid rgba(196,134,106,0.15)`,
          borderTop: `2px solid ${C.terracota}`,
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      padding: '28px 20px 100px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      maxWidth: 460,
      margin: '0 auto',
    }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <LotusIcon size={22} color={C.terracota} />
          <span style={{
            fontSize: '0.72rem',
            color: C.terracota,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            fontWeight: 600,
            opacity: 0.8,
          }}>
            Matrescence
          </span>
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 'clamp(1.4rem, 5.5vw, 1.85rem)',
          color: 'rgba(255,255,255,0.9)',
          margin: 0,
          fontWeight: 500,
          lineHeight: 1.25,
        }}>
          {firstName ? `Welcome, ${firstName}` : 'Welcome'}
        </h1>
        {babyName && (
          <p style={{
            fontSize: '0.85rem',
            color: 'rgba(196,134,106,0.7)',
            margin: '5px 0 0',
            fontStyle: 'italic',
          }}>
            {weeksPostpartum === null ? `Expecting ${babyName}` : `Mother of ${babyName}`}
          </p>
        )}
        <p style={{
          fontSize: '0.78rem',
          color: 'rgba(255,255,255,0.3)',
          margin: '8px 0 0',
          letterSpacing: '0.02em',
        }}>
          {phase}
        </p>
      </div>

      {/* Welcome / Getting Started guide — sessions 1–3 */}
      <WelcomeSheet />

      {/* Night-feed mode */}
      {nightMode && (
        <NightFeedMode userId={user?.user_id} onClose={() => setNightMode(false)} />
      )}
      <button
        onClick={() => setNightMode(true)}
        style={{
          width: '100%',
          padding: '16px 18px',
          background: 'linear-gradient(135deg, rgba(20,12,10,0.9) 0%, rgba(42,24,16,0.9) 100%)',
          border: '1px solid rgba(196,134,106,0.25)',
          borderRadius: 16,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          outline: 'none',
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'rgba(196,134,106,0.1)',
          border: '1px solid rgba(196,134,106,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
            <path d="M14 10.5 A6.5 6.5 0 1 1 6.5 3 A5 5 0 0 0 14 10.5Z"
              stroke={C.terracota} strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.82)', fontWeight: 600 }}>
            Night-feed mode
          </p>
          <p style={{ margin: '3px 0 0', fontSize: '0.73rem', color: 'rgba(255,255,255,0.32)', lineHeight: 1.4 }}>
            2–4 min one-handed practices for the 3am hour. Dim light, no scrolling.
          </p>
        </div>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
          <path d="M4 7H10M7 4L10 7L7 10" stroke="rgba(196,134,106,0.6)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Intention card */}
      <div style={{
        background: 'rgba(196,134,106,0.07)',
        border: '1px solid rgba(196,134,106,0.2)',
        borderRadius: 16,
        padding: '16px 18px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(196,134,106,0.12)',
          border: '1px solid rgba(196,134,106,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="3.5" stroke={C.terracota} strokeWidth="1.3"/>
            <path d="M8 1V3M8 13V15M1 8H3M13 8H15" stroke={C.terracota} strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3 }}>
            Your intention
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255,255,255,0.82)', fontStyle: 'italic', lineHeight: 1.4 }}>
            "{intention}"
          </p>
        </div>
      </div>

      {/* ERI Score */}
      {eriScore !== undefined && eriScore !== null && (
        <div style={{
          background: C.cardBg,
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 16,
          padding: '16px 18px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>
              Emotional Regulation Index
            </p>
            <p style={{ margin: 0, fontSize: '1.7rem', fontWeight: 700, color: C.gold, lineHeight: 1 }}>
              {Math.round(eriScore)}
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.3)', fontWeight: 400, marginLeft: 4 }}>/100</span>
            </p>
          </div>
          <ERIArc score={eriScore} />
        </div>
      )}

      {/* EPDS nudge — always visible, state changes based on whether overdue */}
      {(() => {
        const overdue = epdsOverdue();
        const countdown = epdsCountdownDays();
        const lastScore = epds?.epds_score;
        const lastSeverity = epds?.epds_severity;

        return (
          <button
            onClick={() => overdue ? navigate('/epds') : navigate('/epds-history')}
            style={{
              width: '100%',
              padding: '14px 18px',
              background: overdue ? 'rgba(196,134,106,0.09)' : 'rgba(255,255,255,0.025)',
              border: `1.5px solid ${overdue ? 'rgba(196,134,106,0.32)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 14,
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              textAlign: 'left',
              outline: 'none',
              transition: 'all 0.3s',
            }}
          >
            {/* Icon */}
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: overdue ? 'rgba(196,134,106,0.15)' : 'rgba(255,255,255,0.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {overdue ? (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M7.5 2V8M7.5 11V12" stroke={C.terracota} strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="7.5" cy="7.5" r="6" stroke={C.terracota} strokeWidth="1.2"/>
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <circle cx="7.5" cy="7.5" r="5.5" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2"/>
                  <path d="M7.5 4.5V8L9.5 9.5" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              )}
            </div>

            {/* Text */}
            <div style={{ flex: 1 }}>
              {overdue ? (
                <>
                  <p style={{ margin: 0, fontSize: '0.87rem', color: 'rgba(255,255,255,0.82)', fontWeight: 500 }}>
                    {!epds ? 'Start your first EPDS check-in' : 'Wellbeing check-in due'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>
                    Postnatal wellbeing · 3 min
                  </p>
                </>
              ) : (
                <>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>
                    Next check-in in <span style={{ color: 'rgba(255,255,255,0.62)', fontWeight: 600 }}>{countdown} day{countdown !== 1 ? 's' : ''}</span>
                  </p>
                  {lastScore != null && (
                    <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)' }}>
                      Last score: <span style={{
                        color: lastScore <= 9 ? '#7EC8C8' : lastScore <= 12 ? '#C9A86C' : '#D4756A',
                        fontWeight: 600,
                      }}>{lastScore}/30</span>
                      {' '}· {lastSeverity === 'low' ? 'Low concern' : lastSeverity === 'possible' ? 'Possible' : 'Probable'}
                    </p>
                  )}
                </>
              )}
            </div>

            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M4 7H10M7 4L10 7L7 10"
                stroke={overdue ? 'rgba(196,134,106,0.6)' : 'rgba(255,255,255,0.18)'}
                strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        );
      })()}

      {/* Daily Loop */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel>Today's Practice</SectionLabel>
        <div style={{
          background: C.cardBg,
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 18,
          overflow: 'hidden',
        }}>
          {/* Phase overview — visual only, not clickable individually */}
          <div style={{ padding: '16px 18px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
              {LOOP_PHASES.map((p, i) => (
                <div key={p.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <div style={{
                    width: 28, height: 28,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(196,134,106,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.terracota,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}>
                    {i + 1}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', lineHeight: 1.2 }}>
                    {p.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
          {/* Single CTA */}
          <button
            onClick={() => navigate('/alignment')}
            style={{
              width: '100%',
              padding: '14px 18px',
              background: 'transparent',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              Start today's practice
            </p>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M5 8H11M8 5L11 8L8 11" stroke={C.terracota} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Quick cards */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel>Explore</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
          <QuickCard
            icon={<BrainIcon />}
            title="Journal"
            sub="Process your thoughts"
            onClick={() => navigate('/journal')}
          />
          <QuickCard
            icon={<WaveIcon />}
            title="Studio"
            sub="Matrescence meditations"
            onClick={() => navigate('/studio')}
          />
          <QuickCard
            icon={<ChartIcon />}
            title="Insights"
            sub="Your ERI over time"
            onClick={() => navigate('/insights')}
          />
          <QuickCard
            icon={<HeartIcon />}
            title="EPDS History"
            sub="Wellbeing check-ins"
            onClick={() => navigate('/epds-history')}
          />
        </div>
      </div>

      {/* Last EPDS score */}
      {epds && !epdsOverdue() && (
        <div style={{
          background: C.cardBg,
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 16,
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>
              Last EPDS
            </p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: epdsColor(epds.epds_score), lineHeight: 1 }}>
              {epds.epds_score}
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.28)', fontWeight: 400, marginLeft: 4 }}>/30</span>
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', textTransform: 'capitalize' }}>
              {epds.epds_severity} · {daysAgo(epds.created_at)}
            </p>
          </div>
          <button
            onClick={() => navigate('/epds')}
            style={{
              padding: '8px 14px',
              background: 'rgba(196,134,106,0.1)',
              border: '1px solid rgba(196,134,106,0.25)',
              borderRadius: 20,
              color: C.terracota,
              fontSize: '0.78rem',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            Check again
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function epdsColor(score) {
  if (score <= 9)  return '#7EC8C8';
  if (score <= 12) return '#C9A86C';
  return '#D4756A';
}

function daysAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  return `${diff} days ago`;
}

function SectionLabel({ children }) {
  return (
    <p style={{
      margin: '0 0 10px 2px',
      fontSize: '0.72rem',
      color: 'rgba(255,255,255,0.3)',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}>
      {children}
    </p>
  );
}

function QuickCard({ icon, title, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'rgba(255,255,255,0.038)',
        border: '1px solid rgba(196,134,106,0.15)',
        borderRadius: 16,
        padding: '16px 14px',
        cursor: 'pointer',
        textAlign: 'left',
        outline: 'none',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ marginBottom: 10, color: 'rgba(196,134,106,0.7)' }}>{icon}</div>
      <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.82)', fontWeight: 600 }}>{title}</p>
      <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', lineHeight: 1.3 }}>{sub}</p>
    </button>
  );
}

function ERIArc({ score }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  return (
    <svg width="58" height="58" viewBox="0 0 58 58">
      <circle cx="29" cy="29" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="3" fill="none"/>
      <circle
        cx="29" cy="29" r={r}
        stroke="url(#matGrad)"
        strokeWidth="3"
        fill="none"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 29 29)"
      />
      <defs>
        <linearGradient id="matGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#C4866A"/>
          <stop offset="100%" stopColor="#D4A5A0"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// Micro icons
const BrainIcon = () => <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M5 9c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M9 5V3M9 15v-2M3 9H1M17 9h-2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.6"/></svg>;
const WaveIcon  = () => <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M1 9 C3 6 5 6 6 9 C7 12 9 12 10 9 C11 6 13 6 15 9 C16 11 17 11 17 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/></svg>;
const ChartIcon = () => <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><polyline points="2,14 6,8 10,11 14,5 16,7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;
const HeartIcon = () => <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 14.5 C9 14.5 2.5 10 2.5 6 C2.5 3.8 4.3 2.5 6.5 3 C7.7 3.3 8.6 4 9 4.8 C9.4 4 10.3 3.3 11.5 3 C13.7 2.5 15.5 3.8 15.5 6 C15.5 10 9 14.5 9 14.5Z" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinejoin="round"/></svg>;

function LotusIcon({ size = 24, color = '#C4866A' }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
      <path d="M18 28 C18 28 12 22 12 16 C12 11 15 8 18 8 C21 8 24 11 24 16 C24 22 18 28 18 28Z"
        stroke={color} strokeWidth="1.3" fill="none" opacity="0.85"/>
      <path d="M18 28 C18 28 8 24 7 18 C6 13 9 10 12 11 C14 12 15 14 15 16"
        stroke={color} strokeWidth="1.1" fill="none" opacity="0.5"/>
      <path d="M18 28 C18 28 28 24 29 18 C30 13 27 10 24 11 C22 12 21 14 21 16"
        stroke={color} strokeWidth="1.1" fill="none" opacity="0.5"/>
    </svg>
  );
}
